import {useQuery} from '@apollo/client';
import {
  Alert,
  Button as AntButton,
  Dropdown,
  Form,
  Pagination,
  Table,
  Tooltip,
} from 'antd';
import {GET_ALL_ORDERS} from 'graphql/queries/stocks';
import {useLocation, useNavigate} from 'react-router-dom';
import dotsIcon from 'icons/dots.svg';
import {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import styles from './viewOrders.module.scss';
import theme from '../ordersTheme.module.scss';
import {Search} from '@dabafinance/react-components';
import gamer from 'images/gamer.png';
import filterIcon from 'icons/filter.svg';

import {OrderFilter} from './OrderFilter';
import moment from 'moment';
import {
  CopyOutlined,
  DownOutlined,
  RetweetOutlined,
  SelectOutlined,
} from '@ant-design/icons';
import {
  BulkFillCSVOrders,
  BulkFillOrder,
  ExportBroker,
  FilledOrder,
  OpenOrder,
  OrderHistory,
  OrderNotes,
  RejectOrder,
  RevertOpenOrder,
} from './HandleOrders';
import {toast} from 'react-toastify';
import {useSelector} from 'react-redux';
import {makeSelectTabOperations} from 'redux/store/auth';
import {NavTabsEnum} from 'utils/constants';
import ManualTriggerEmail from './ManualTriggerEmail';
import {
  bulkOperationItems,
  handleSearchStockTicker,
  handleStocksOrderFilter,
  handleStocksOrdersColumns,
  HoldOrderModal,
} from './OrderActions';
import DBModal from 'components/modal/modal';
import {OrderBookModal} from './OrderBookModal';
import {ViewBrokerOrderIds} from './ViewBrokerOrderIds';
import {OrderActionsModal} from './orderActionsModal';
import {PlatformPortfolio} from './PlatformPortfolio';
import {BookToOrderBookModal} from './BookToOrderBookModal';
import {BrokerDownloadOptionsModal} from './BrokerDownloadOptionsModal';
import {OrderBookTrigger} from './OrderBookTrigger';
import {SettleOptionsModal} from './SettleOptionsModal';
import {AutoExecuteOptionsModal} from './AutoExecuteOptionsModal';
import {AutoExecuteModal} from './AutoExecuteModal';
import BrokerPortfolioModal from './BrokerPortfolioModal';
import {CloseOutlined} from '@ant-design/icons';
import {useHistoryContextStore} from '../../../../zustand/historyContextStore';
import {useIsSuperAdmin} from 'hooks';
import {summarizeOrderFilters} from './orderFilterSummary';
import {Pill} from './ui/cells';
import {OrderRowDetail} from './ui/OrderRowDetail';
import {SavedViews, decodeView, readDefaultView} from './ui/SavedViews';
import {ExchangeScopeSelector} from 'pages/exchanges/ExchangeScopeSelector';
import {OrderExchangeContext} from 'pages/exchanges/OrderExchangeContext';
import {useExchangeScope} from 'pages/exchanges/useExchangeScope';
import {useSendUserNotification} from 'components/sendUserNotification';
import TableTopScrollbar from 'components/tableTopScrollbar';

// Friendly titles for saved history entries, per order tab.
const ORDER_TAB_TITLES = {
  ACTIVE: 'Open Orders',
  PENDING: 'Pending Orders',
  FILLED: 'Filled Orders',
  HOLD: 'Orders on Hold',
  PAST: 'Past Orders',
  EXPIRED: 'Expired Orders',
};

const ViewOrders = ({activeTab, isAffiliate, onTotalChange}) => {
  const [orderModal, setOrderModal] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [orderId, setOrderId] = useState('');
  const [filterValues, setFilterValues] = useState({});

  // The shared exchange lens. Orders carry `stockExchange` as a bare ID, so
  // the venue list doubles as the id -> code lookup for the column.
  const scope = useExchangeScope();
  const {exchangeId} = scope;
  const venueById = useMemo(
    () =>
      Object.fromEntries(
        (scope.exchanges ?? []).map(node => [node.exchange.id, node.exchange]),
      ),
    [scope.exchanges],
  );
  const [orderDetails, setOrderDetails] = useState({});
  const [user, setUser] = useState(null);
  const [limit, setLimit] = useState(10);
  const [searchedValue, setSearchedValue] = useState('');
  const [tableData, setTableData] = useState([]);
  const [openFilterModal, setOpenFilterModal] = useState(false);
  const [expandedRowKeys, setExpandedRowKeys] = useState([]);
  const [selectedRows, setSelectedRows] = useState([]);
  const [selectedOrdersInfo, setSelectedOrdersInfo] = useState([]);
  const [operationType, setOperationType] = useState('SINGLE');
  const [isMarketOrderSelected, setIsMarketOrderSelected] = useState(false);
  const [openExportModal, setOpenExportModal] = useState({
    open: false,
    type: null,
  });
  const [openBulkFillModal, setOpenBulkFillModal] = useState(false);
  const [manualTriggerModal, setManualTriggerModal] = useState(false);
  const [hasMounted, setHasMounted] = useState(false);
  const [copied, setCopied] = useState(false);
  const [keyAlert, setKeyAlert] = useState({
    visible: false,
    key: '',
    message: '',
  });
  const [showOrderBook, setShowOrderBook] = useState(false);
  const [orderbookAction, setOrderbookAction] = useState({
    open: false,
    type: null,
  });
  const [autoExecuteAssetType, setAutoExecuteAssetType] = useState(null);
  const [bookToOrderBookModal, setBookToOrderBookModal] = useState({
    open: false,
    type: null,
  });
  const [isBrokerModalOpen, setIsBrokerModalOpen] = useState(false);
  const [platformPortfolioForm] = Form.useForm();
  const [bookToOrderBookForm] = Form.useForm();
  const [orderBookTriggerForm] = Form.useForm();
  const [autoExecuteForm] = Form.useForm();

  const navigate = useNavigate();
  const userRole = useSelector(
    state => state?.auth?.userData?.user?.activeAdminTab?.name,
  );
  const route = useLocation();

  const searchParams = new URLSearchParams(route.search);
  const searchTicker = searchParams.get('ticker');
  const instrumentId = searchParams.get('instrumentId');

  const adminId = useSelector(state => state?.auth?.userData?.user?.id);
  const tabOperations = useSelector(state =>
    makeSelectTabOperations(state, NavTabsEnum.StockListings),
  );

  const handleTab = (tab, id) => {
    if (
      [
        'FILLED',
        'PENDING',
        'HISTORY',
        'ONHOLD',
        'NOTES',
        'HOLD',
        'EXECUTE',
      ].includes(tab)
    ) {
      setOrderModal(tab);
      setOrderDetails(id);
    } else if (tab === 'VIEW_ORDER') {
      navigate(`/dashboard/stocks/view-order/${id}`);
    } else if (tab === 'ORDER_IDS') {
      setOrderModal(tab);
      setOrderDetails(id);
    } else if (tab !== 'SETTLE') {
      setOrderModal(tab);
    } else {
      navigate(`/dashboard/stocks/settle-order/${id}`);
    }
    setOrderId(id);
  };

  const {canSendNotification, openSendNotification, sendNotificationModal} =
    useSendUserNotification();

  const baseColumns = (() =>
    handleStocksOrdersColumns(
      navigate,
      styles,
      gamer,
      tabOperations,
      handleTab,
      setCopied,
      copied,
      dotsIcon,
      {allowed: canSendNotification, open: openSendNotification},
    ))();

  // Exchange is always shown — an operator reading a queue wants the venue on
  // the row even when the page is already scoped to it. Asset class stays
  // scope-dependent, since it is the wider of the two distinctions.
  const columns = [
    ...baseColumns.slice(0, 1),
    {
      title: 'Exchange',
      key: 'exchange',
      width: 110,
      render: (text, record) => {
        const venue = venueById[record?.stockExchange];
        return venue ? (
          <Tooltip title={venue.stockExchangeName}>
            <b className={theme.ticker}>{venue.code}</b>
          </Tooltip>
        ) : (
          <span className={theme.muted}>—</span>
        );
      },
    },
    ...(exchangeId
      ? []
      : [
          {
            title: 'Asset class',
            key: 'assetClass',
            width: 150,
            render: (text, record) =>
              record?.assetClass ? (
                <Pill>{record.assetClass.replace(/_/g, ' ')}</Pill>
              ) : (
                <span className={theme.muted}>—</span>
              ),
          },
        ]),
    ...baseColumns.slice(1),
  ];

  const handleBulkOperations = (tab, type) => {
    if (
      ['PENDING', 'HISTORY', 'NOTES', 'HOLD', 'ONHOLD', 'REJECT'].includes(tab)
    ) {
      setOrderModal(tab);
      setOperationType('MULTIPLE');
    } else if (!['SETTLE', 'FILLED'].includes(tab)) {
      setOrderModal(tab);
      setOrderId({type});
      setOperationType('MULTIPLE');
    } else {
      setOpenBulkFillModal({isOpen: true, type: tab});
    }
  };

  const rowSelection = {
    selectedRowKeys: selectedRows,
    onChange: (selectedRowKeys, selectedRows) => {
      const hasMarketOrder = selectedRows?.some(data => data.type === 'MARKET');
      setIsMarketOrderSelected(hasMarketOrder);
      setSelectedRows(selectedRowKeys);
      setSelectedOrdersInfo(selectedRows);
    },
  };

  const {loading, refetch, networkStatus} = useQuery(GET_ALL_ORDERS, {
    variables: {
      input: {
        page: 1,
        hold: activeTab === 'HOLD',
        status: ['PENDING', 'FILLED', 'EXPIRED'].includes(activeTab)
          ? activeTab
          : null,
        'paginate': true,
        'limit': 10,
        ...(searchTicker && {search: searchTicker}),
        ...(instrumentId && {instrumentId}),
        'newFirst': true,
        ...(isAffiliate && {affiliateOrder: isAffiliate}),
        ...(exchangeId && {stockExchange: exchangeId}),

        includeOrdersWithElapsedExpiry: true,
        openOrders:
          activeTab === 'ACTIVE'
            ? true
            : ['PENDING', 'FILLED', 'HOLD', 'EXPIRED'].includes(activeTab)
            ? null
            : false,
      },
    },
    fetchPolicy: 'no-cache',
    onCompleted: data => setTableData(data?.getOrders),
    errorPolicy: 'ignore',
    notifyOnNetworkStatusChange: true,
    onError: error => toast.error(error.message),
  });

  const handleResetPage = () => setCurrentPage(1);

  // The tab rail shows a count for the tab in view; it comes from the page’s
  // own pagination, so no extra query is issued for it.
  useEffect(() => {
    onTotalChange?.(tableData?.pagination?.total ?? null);
  }, [tableData, onTotalChange]);

  useEffect(() => {
    setFilterValues({});
    handleResetPage();
    setSearchedValue('');
    setSelectedOrdersInfo([]);
    setSelectedRows([]);
    setLimit(10);
  }, [activeTab]);

  useEffect(() => {
    if (!hasMounted) {
      setHasMounted(true);
      return;
    }
    handleStocksOrderFilter(
      filterValues,
      setFilterValues,
      // A page-size change restarts the list.
      1,
      user,
      refetch,
      activeTab,
      limit,
      searchTicker,
      searchedValue,
      isAffiliate,
      setOpenFilterModal,
      instrumentId,
      scope.exchangeId,
    );
  }, [limit]);

  const debounce = func => {
    let timer;
    return function (...args) {
      const context = this;
      if (timer) clearTimeout(timer);
      timer = setTimeout(() => {
        timer = null;
        func.apply(context, args);
      }, 1200);
    };
  };

  const handleResetParamsOnPage = () => {
    const searchParams = new URLSearchParams(window.location.search);
    searchParams.delete('ticker');
    const newUrl = window.location.pathname + '?' + searchParams.toString();
    window.location.href = window.location.origin + newUrl; // Reload the page with the updated URL
  };

  const optimizedVersion = useCallback(debounce(handleSearchStockTicker), []);

  const handleKeyCopied = useCallback((key, context) => {
    if (!key) return;
    setKeyAlert({
      visible: true,
      key,
      message: context
        ? `${context} key copied to clipboard.`
        : 'Order book key copied to clipboard.',
    });
  }, []);

  const handlePageChange = page => {
    if (page === currentPage) return;
    setCurrentPage(page);
    handleStocksOrderFilter(
      filterValues,
      setFilterValues,
      page,
      user,
      refetch,
      activeTab,
      limit,
      searchTicker,
      searchedValue,
      isAffiliate,
      setOpenFilterModal,
      instrumentId,
      scope.exchangeId,
    );
  };

  // ── History: expose current filters, and re-apply saved ones on revisit ──
  const setPageDescriptor = useHistoryContextStore(
    state => state.setPageDescriptor,
  );
  const clearPageDescriptor = useHistoryContextStore(
    state => state.clearPageDescriptor,
  );
  const isSuperAdmin = useIsSuperAdmin();

  // Hand the current filters up to the floating "Add to history" button so a
  // saved snapshot can restore them. Filters live in local state — this only
  // mirrors them for capture. Super-admin only.
  useEffect(() => {
    if (!isSuperAdmin) return undefined;
    const summary = summarizeOrderFilters(filterValues, searchedValue, user);
    setPageDescriptor({
      path: route.pathname,
      title: ORDER_TAB_TITLES[activeTab] || 'Orders',
      subtitle: 'Trading View',
      context: {scope: 'page', tabKey: 'Orders', activeTab},
      filters: summary.length
        ? {count: summary.length, summary, filterValues, searchedValue, user}
        : null,
    });
    return () => clearPageDescriptor();
  }, [
    isSuperAdmin,
    filterValues,
    searchedValue,
    user,
    activeTab,
    route.pathname,
    setPageDescriptor,
    clearPageDescriptor,
  ]);

  const applyOrderFilters = (nextValues, nextSearch, nextUser) => {
    // The request below always asks for page 1, so the control has to agree.
    // Without this, removing a chip on page 5 fetched page 1 while the pager
    // still read 5 — and the next click went to page 4 of a list that had
    // already restarted.
    setCurrentPage(1);
    handleStocksOrderFilter(
      nextValues,
      setFilterValues,
      1,
      nextUser,
      refetch,
      activeTab,
      limit,
      searchTicker,
      nextSearch,
      isAffiliate,
      setOpenFilterModal,
      instrumentId,
      scope.exchangeId,
    );
  };

  const restoreView = view => {
    if (!view) return;
    setUser(view.user ?? null);
    setSearchedValue(view.searchedValue ?? '');
    setFilterValues(view.filterValues ?? {});
    applyOrderFilters(
      view.filterValues ?? {},
      view.searchedValue ?? '',
      view.user ?? null,
    );
  };

  // A restore from history arrives with filters in navigation state. Apply them
  // (updating the filter state + the request) before showing the tab.
  useEffect(() => {
    const hf = route.state?.historyFilters;
    if (hf && (hf.filterValues || hf.searchedValue || hf.user)) {
      if (hf.user !== undefined) setUser(hf.user || null);
      setSearchedValue(hf.searchedValue || '');
      setFilterValues(hf.filterValues || {});
      applyOrderFilters(hf.filterValues || {}, hf.searchedValue || '', hf.user);
      // Consume the one-shot state so it doesn't re-apply on refresh.
      window.history.replaceState({}, document.title);
    }
  }, [route.key]);

  // A shared view arrives in the URL, and a pinned view is what this tab
  // should open with. The link wins — someone following it asked for that
  // view specifically — and both are one-shot, so a later manual change is
  // never undone.
  const appliedEntryView = useRef(null);
  useEffect(() => {
    if (appliedEntryView.current === activeTab) return;
    appliedEntryView.current = activeTab;

    const shared = new URLSearchParams(route.search).get('orderView');
    if (shared) {
      const decoded = decodeView(shared);
      if (decoded) {
        restoreView(decoded);
        toast.success(
          `Applied the shared view${decoded.name ? ` "${decoded.name}"` : ''}`,
        );
        return;
      }
      toast.error('That shared view link could not be read');
      return;
    }

    const pinned = readDefaultView(adminId, activeTab);
    if (pinned) restoreView(pinned);
  }, [activeTab, route.search]);

  const appliedFilterChips = summarizeOrderFilters(
    filterValues,
    searchedValue,
    user,
  );

  const handleRemoveFilter = chipKey => {
    if (chipKey === '__search') {
      setSearchedValue('');
      applyOrderFilters(filterValues, '', user);
      return;
    }
    if (chipKey === '__user') {
      setUser(null);
      applyOrderFilters(filterValues, searchedValue, null);
      return;
    }
    const next = {...filterValues};
    delete next[chipKey];
    applyOrderFilters(next, searchedValue, user);
  };

  const handleClearAllFilters = () => {
    setSearchedValue('');
    setUser(null);
    applyOrderFilters({}, '', null);
  };

  if (activeTab === 'PAST') {
    columns.splice(17, 0, {
      title: 'Settled Date',
      key: 'settledDate',
      dataIndex: 'settledDate',
      width: 180,
      render: (text, record) =>
        record?.settlementDate ? (
          <span className={theme.tabular}>
            {moment(record?.settlementDate).format('DD MMM YYYY, h:mmA')}
          </span>
        ) : (
          <span className={theme.muted}>—</span>
        ),
    });
  }

  if (['ACTIVE', 'FILLED', 'PAST', 'EXPIRED'].includes(activeTab)) {
    columns.splice(4, 0, {
      title: 'Execution',
      key: 'execution',
      dataIndex: 'execution',
      width: 160,
      render: (text, render) => (
        <div className={theme.execution}>
          <Pill tone={render?.brokerIds?.length > 0 ? 'ok' : 'neutral'}>
            {render?.brokerIds?.length > 0 ? 'AUTOMATIC' : 'MANUAL'}
          </Pill>
          <Tooltip title="Broker order IDs">
            <SelectOutlined
              className={theme.executionOpen}
              onClick={e => {
                e.stopPropagation();
                handleTab('ORDER_IDS', render, e);
              }}
            />
          </Tooltip>
        </div>
      ),
    });
  }

  if (activeTab === 'FILLED') {
    columns.splice(17, 0, {
      title: 'Broker',
      key: 'broker',
      dataIndex: 'broker',
      width: 160,
      render: (text, record) =>
        record?.broker?.name ? (
          <span>{record.broker.name}</span>
        ) : (
          <span className={theme.muted}>—</span>
        ),
    });
  }

  // A note is worth marking, but repainting the whole row grey read as an
  // error state; a left accent says "annotated" without shouting.
  const rowClassName = record => (record.notes ? theme.noteRow : '');

  // Every column carries an explicit width, so the sum is the scroll width —
  // which is also what makes the pinned first and last columns engage.
  const scrollX = columns.reduce(
    (total, column) => total + (column.width || 140),
    0,
  );

  const memoizedBulkOperationItems = useMemo(
    () =>
      bulkOperationItems(
        activeTab,
        handleBulkOperations,
        !selectedRows?.length,
        setOrderModal,
        isMarketOrderSelected,
      ),
    [activeTab, selectedRows?.length, isMarketOrderSelected],
  );

  const hasSelection = selectedRows.length > 0;

  // Shared by the sticky pager above the table and the one below it.
  const pagerProps = {
    current: currentPage,
    total: tableData.pagination?.total,
    pageSize: limit,
    showQuickJumper: true,
    showSizeChanger: true,
    onChange: e => handlePageChange(e),
    onShowSizeChange: (_, size) => {
      // A larger page size makes the page you were on meaningless: page 8 of
      // 10-per-page does not exist at 100 per page, and the table came back
      // empty while the pager still showed 8.
      setCurrentPage(1);
      setLimit(size);
    },
  };
  const bulkOperationsVisible =
    tabOperations?.update && !['EXPIRED', 'PAST'].includes(activeTab);

  const orderbookActionItems = [
    {
      key: 1,
      label: 'Broker Order Export',
      disabled: loading || networkStatus === 2,
      onClick: () => setOpenExportModal({open: true, type: 'ORDER'}),
    },
    {
      key: 6,
      label: 'Broker Back Office Export',
      disabled: loading || networkStatus === 2,
      onClick: () => setOpenExportModal({open: true, type: 'BACK_OFFICE'}),
    },
    {
      key: 2,
      label: 'Broker Statement / History',
      disabled: !tabOperations?.export,
      onClick: () =>
        setOrderbookAction({
          open: true,
          type: 'BROKER_DOWNLOAD_OPTIONS',
        }),
    },
    {
      key: 3,
      label: 'Broker Portfolio & Cash Balance',
      onClick: () => {
        setIsBrokerModalOpen(true);
      },
    },
    {
      key: 7,
      label: 'Broker Book to Order Book',
      disabled: !tabOperations?.export,
      onClick: () =>
        setBookToOrderBookModal({
          open: true,
          type: 'BOOK_TO_ORDER_BOOK',
        }),
    },
    {
      key: 4,
      label: 'Platform Portfolio',
      onClick: () => {
        setOrderbookAction({open: true, type: 'PORTFOLIO'});
      },
    },

    {
      key: 5,
      label: 'Auto Triggers',
      disabled:
        !tabOperations?.update ||
        !['SUPERADMIN', 'ADMIN', 'FINANCIAL_OPERATOR'].includes(userRole),
      onClick: () => {
        setOrderbookAction({
          open: true,
          type: 'SETTLE_OPTIONS',
        });
      },
    },
    {
      key: 8,
      label: 'Execute Order(s)',
      onClick: () => {
        setOrderbookAction({open: true, type: 'AUTO_EXECUTE'});
      },
    },
  ];

  return (
    <div className={theme.body}>
      <div className={theme.venueRow}>
        <ExchangeScopeSelector {...scope} />
      </div>
      <OrderExchangeContext exchangeId={exchangeId} />

      <div className={theme.toolbar}>
        {searchTicker && (
          <span className={theme.tickerChip}>
            {searchTicker}
            <button
              type="button"
              className={theme.tickerChipClose}
              aria-label={`Stop filtering by ${searchTicker}`}
              onClick={() => handleResetParamsOnPage()}>
              <CloseOutlined />
            </button>
          </span>
        )}

        <div className={theme.search}>
          <Search
            placeholder="Search by Ticker/Status"
            value={searchedValue}
            onChange={e => {
              setSearchedValue(e);
              optimizedVersion(
                e,
                user,
                activeTab,
                limit,
                filterValues,
                refetch,
                currentPage,
                isAffiliate,
                handleResetPage,
              );
            }}
            id={'user-search'}
          />
        </div>

        <div className={theme.toolbarRight}>
          <button
            type="button"
            onClick={() => setOpenFilterModal(true)}
            className={`${theme.control} ${
              appliedFilterChips.length ? theme.controlOn : ''
            }`}>
            <img src={filterIcon} alt="" />
            Filter
            {appliedFilterChips.length > 0 && (
              <span className={theme.controlCount}>
                {appliedFilterChips.length}
              </span>
            )}
          </button>

          <SavedViews
            activeTab={activeTab}
            filterValues={filterValues}
            searchedValue={searchedValue}
            user={user}
            hasFilters={appliedFilterChips.length > 0}
            onApply={view => {
              setUser(view.user ?? null);
              setSearchedValue(view.searchedValue ?? '');
              setFilterValues(view.filterValues ?? {});
              applyOrderFilters(
                view.filterValues ?? {},
                view.searchedValue ?? '',
                view.user ?? null,
              );
            }}
          />

          <Tooltip title="Refresh">
            <button
              type="button"
              aria-label="Refresh orders"
              className={`${theme.control} ${theme.controlIcon}`}
              onClick={() => refetch()}>
              <RetweetOutlined />
            </button>
          </Tooltip>

          <button
            type="button"
            className={theme.control}
            onClick={() => setShowOrderBook(true)}>
            Order Book Status
          </button>

          {bulkOperationsVisible && (
            <Dropdown
              trigger={['click']}
              overlayClassName={theme.menu}
              menu={{
                items: memoizedBulkOperationItems,
              }}>
              <button
                type="button"
                className={theme.control}
                onClick={e => e.stopPropagation()}>
                Bulk Operation
                <DownOutlined style={{fontSize: 10}} />
              </button>
            </Dropdown>
          )}

          {activeTab === 'EXPIRED' && tabOperations?.update ? (
            <button
              type="button"
              className={`${theme.control} ${theme.controlPrimary}`}
              onClick={() => setManualTriggerModal(true)}>
              Export Expiring Order
            </button>
          ) : (
            tabOperations?.export && (
              <Dropdown
                overlayClassName={theme.menu}
                menu={{
                  items: orderbookActionItems,
                }}>
                <button
                  type="button"
                  className={`${theme.control} ${theme.controlPrimary}`}>
                  Orderbook Actions
                  <DownOutlined style={{fontSize: 10}} />
                </button>
              </Dropdown>
            )
          )}
        </div>
      </div>

      {appliedFilterChips.length > 0 && (
        <div className={theme.chips}>
          <span className={theme.chipsLabel}>Filters</span>
          {appliedFilterChips.map(chip => (
            <span key={chip.key} className={theme.chip}>
              <span className={theme.chipLabel}>{chip.label}</span>
              {chip.value && (
                <span className={theme.chipValue}>{chip.value}</span>
              )}
              <button
                type="button"
                className={theme.chipRemove}
                aria-label={`Remove ${chip.label} filter`}
                onClick={() => handleRemoveFilter(chip.key)}>
                <CloseOutlined />
              </button>
            </span>
          ))}
          <button
            type="button"
            className={theme.chipsClear}
            onClick={handleClearAllFilters}>
            Clear all
          </button>
        </div>
      )}

      {keyAlert.visible && (
        <Alert
          banner
          closable
          showIcon
          type="warning"
          message={keyAlert.message}
          description={
            <div style={{display: 'flex', alignItems: 'center', gap: 12}}>
              <span style={{color: 'rgba(0, 0, 0, 0.65)'}}>
                Use the copy icon to copy the key again.
              </span>
              <Tooltip title="Copy key again">
                <AntButton
                  type="text"
                  icon={<CopyOutlined />}
                  onClick={() => {
                    if (keyAlert.key) {
                      navigator.clipboard.writeText(keyAlert.key);
                    }
                  }}
                />
              </Tooltip>
            </div>
          }
          onClose={() => setKeyAlert({visible: false, key: '', message: ''})}
          style={{marginBottom: 16}}
        />
      )}

      {hasSelection && (
        <div className={theme.selectionBar}>
          <span className={theme.selectionCount}>
            {selectedRows.length} selected
          </span>
          <button
            type="button"
            className={theme.selectionClear}
            onClick={() => {
              setSelectedRows([]);
              setSelectedOrdersInfo([]);
              setIsMarketOrderSelected(false);
            }}>
            Clear selection
          </button>
          {bulkOperationsVisible && (
            <span className={theme.selectionHint}>
              Choose an action from Bulk Operation
            </span>
          )}
        </div>
      )}

      {/* The same pager as the one under the table, pinned below the app
          header so paging a long order book does not mean scrolling to the
          bottom and back. Outside the surface card on purpose — that element
          clips its overflow, which would confine the sticky positioning. */}
      <TableTopScrollbar pagerProps={pagerProps} />
      <div
        className={`${theme.surface} ${
          hasSelection ? theme.surfaceSelected : ''
        }`}>
        <Table
          rowSelection={{
            type: 'checkbox',
            fixed: true,
            ...rowSelection,
          }}
          expandable={{
            fixed: 'left',
            columnWidth: 44,
            expandedRowKeys,
            expandedRowRender: record => <OrderRowDetail record={record} />,
            onExpand: (expanded, record) =>
              setExpandedRowKeys(
                expanded
                  ? [...expandedRowKeys, record.key]
                  : expandedRowKeys.filter(key => key !== record.key),
              ),
          }}
          loading={loading || networkStatus === 2}
          dataSource={tableData?.data?.map(item => ({...item, key: item?.id}))}
          columns={columns}
          rowClassName={rowClassName}
          pagination={false}
          sticky
          scroll={{x: scrollX}}
          className={`antd-table ${theme.table}`}
          locale={{
            emptyText: (
              <div className={theme.empty}>
                <h3>No orders here</h3>
                <p>
                  Nothing matches this tab
                  {appliedFilterChips.length > 0
                    ? ' with the filters you have applied.'
                    : ' right now.'}
                </p>
              </div>
            ),
          }}
        />
        <div className={theme.pagination}>
          <Pagination {...pagerProps} />
        </div>
      </div>

      <DBModal
        isOpen={openFilterModal}
        handleClose={() => setOpenFilterModal(false)}
        width={900}
        content={
          <OrderFilter
            refetch={refetch}
            isFilterModalOpen={openFilterModal}
            appliedValues={filterValues}
            user={user}
            setUser={setUser}
            handleFilter={values =>
              handleStocksOrderFilter(
                values,
                setFilterValues,
                null,
                user,
                refetch,
                activeTab,
                limit,
                searchTicker,
                searchedValue,
                isAffiliate,
                setOpenFilterModal,
                // This call site never passed searchInstrumentId, so the
                // slot has to be held explicitly or the lens id lands in it.
                null,
                scope.exchangeId,
              )
            }
            handleClose={() => setOpenFilterModal(false)}
            currentPage={currentPage}
            reset={handleResetPage}
            activeTab={activeTab}
          />
        }
      />

      <BulkFillCSVOrders
        orderModal={orderModal}
        setOrderModal={setOrderModal}
        onKeyCopied={handleKeyCopied}
      />
      <BookToOrderBookModal
        orderModal={bookToOrderBookModal}
        setOrderModal={setBookToOrderBookModal}
        form={bookToOrderBookForm}
        onKeyCopied={handleKeyCopied}
      />

      <RejectOrder
        orderId={orderId}
        orderModal={['REJECT', 'REWRITE'].includes(orderModal)}
        orderIds={selectedRows}
        isReject={orderModal === 'REJECT'}
        setType={setOperationType}
        setSelected={setSelectedRows}
        setOrder={setOrderId}
        setOrderModal={setOrderModal}
        refetch={refetch}
        type={operationType}
        page={currentPage}
        onKeyCopied={handleKeyCopied}
      />
      <HoldOrderModal
        holdStatus={orderModal}
        orderId={orderDetails}
        setOrder={setOrderId}
        setOrderModal={setOrderModal}
        setOrderDetails={setOrderDetails}
        refetch={refetch}
        orderIds={selectedRows}
        clearOrderIds={() => setSelectedRows([])}
        operationType={operationType}
        clearOperationType={() => setOperationType('SINGLE')}
        orderModal={['HOLD', 'ONHOLD'].includes(orderModal)}
      />
      <FilledOrder
        orderModal={orderModal === 'FILLED'}
        setOrderModal={setOrderModal}
        orderDetails={orderDetails}
        refetch={refetch}
        page={currentPage}
        operationType={operationType}
        orderIds={selectedRows}
        setType={setOperationType}
        setSelected={setSelectedRows}
      />
      <OpenOrder
        orderModal={['PENDING', 'EXECUTE'].includes(orderModal)}
        setOrderModal={setOrderModal}
        orderId={orderId}
        refetch={refetch}
        orderType={orderModal}
        page={currentPage}
        orderDetails={orderDetails}
        type={operationType}
        orderIds={selectedRows}
        setType={setOperationType}
        setSelected={setSelectedRows}
      />
      <RevertOpenOrder
        orderModal={orderModal === 'OPEN'}
        setOrderModal={setOrderModal}
        order={orderId}
        setOrder={setOrderId}
        refetch={refetch}
        page={currentPage}
        type={operationType}
        orderIds={selectedRows}
        setType={setOperationType}
        setSelected={setSelectedRows}
      />
      {/* Export broker modal */}
      <ExportBroker
        activeTab={activeTab}
        orderModal={openExportModal.open}
        modalType={openExportModal.type}
        setOrderModal={setOpenExportModal}
      />
      {/* bulk fill modal */}
      <BulkFillOrder
        orderModal={openBulkFillModal}
        setOrderModal={setOpenBulkFillModal}
        refetch={refetch}
        orders={selectedOrdersInfo}
        setOrders={setSelectedOrdersInfo}
        setSelected={setSelectedRows}
        onKeyCopied={handleKeyCopied}
      />
      <OrderHistory
        orderModal={orderModal === 'HISTORY'}
        setOrderModal={setOrderModal}
        history={orderDetails}
      />
      <OrderNotes
        orderModal={orderModal === 'NOTES'}
        setOrderModal={setOrderModal}
        notes={orderDetails}
        refetch={refetch}
      />
      <ManualTriggerEmail
        isOpen={manualTriggerModal}
        onClose={() => setManualTriggerModal(false)}
      />
      <DBModal
        isOpen={showOrderBook}
        handleClose={() => setShowOrderBook(false)}
        content={
          <OrderBookModal
            open={showOrderBook}
            close={() => setShowOrderBook(false)}
          />
        }
      />
      <DBModal
        isOpen={orderModal === 'ORDER_IDS'}
        handleClose={() => {
          setOrderModal(null);
          setOrderDetails({});
        }}
        content={<ViewBrokerOrderIds data={orderDetails} />}
      />
      <DBModal
        isOpen={orderbookAction.open}
        handleClose={() => {
          setOrderbookAction({open: false, type: null});
          setAutoExecuteAssetType(null);
          platformPortfolioForm.resetFields();
          if (
            ['TRIGGER', 'OPEN', 'RETRY', 'REJECTED', 'EXPIRED'].includes(
              orderbookAction.type,
            )
          ) {
            orderBookTriggerForm.resetFields();
          }
          if (orderbookAction.type === 'AUTO_EXECUTE') {
            autoExecuteForm.resetFields();
            // Reset activeTab state by remounting component
            setAutoExecuteAssetType(null);
          }
        }}
        width={
          orderbookAction.type === 'PORTFOLIO'
            ? 1100
            : orderbookAction.type === 'AUTO_EXECUTE'
            ? 800
            : 700
        }
        content={(() => {
          switch (orderbookAction.type) {
            case 'PORTFOLIO':
              return (
                <PlatformPortfolio
                  open={orderbookAction.open}
                  type={orderbookAction.type}
                  scopedExchangeId={exchangeId}
                  form={platformPortfolioForm}
                  close={() => {
                    setOrderbookAction({open: false, type: null});
                    platformPortfolioForm.resetFields();
                  }}
                />
              );
            case 'SETTLE_OPTIONS':
              return (
                <SettleOptionsModal
                  onSelect={type => {
                    if (type === 'SETTLE') {
                      setOrderbookAction({open: true, type: 'SETTLE'});
                    } else {
                      setOrderbookAction({open: true, type: type});
                    }
                  }}
                />
              );
            case 'BROKER_DOWNLOAD_OPTIONS':
              return (
                <BrokerDownloadOptionsModal
                  onSelect={type => {
                    setOrderbookAction({open: true, type: type});
                  }}
                />
              );
            case 'AUTO_EXECUTE':
              return autoExecuteAssetType ? (
                <AutoExecuteModal
                  assetType={autoExecuteAssetType}
                  form={autoExecuteForm}
                  onClose={() => {
                    setOrderbookAction({open: false, type: null});
                    setAutoExecuteAssetType(null);
                    autoExecuteForm.resetFields();
                  }}
                />
              ) : (
                <AutoExecuteOptionsModal
                  onSelect={assetType => {
                    setAutoExecuteAssetType(assetType);
                  }}
                />
              );
            case 'TRIGGER':
            case 'OPEN':
            case 'RETRY':
            case 'REJECTED':
            case 'EXPIRED':
            case 'PENDING':
              return (
                <OrderBookTrigger
                  form={orderBookTriggerForm}
                  goBack={() => {
                    setOrderbookAction({open: true, type: 'SETTLE_OPTIONS'});
                  }}
                  close={() => {
                    setOrderbookAction({open: false, type: null});
                    orderBookTriggerForm.resetFields();
                  }}
                  loading={loading || networkStatus === 2}
                  triggerType={orderbookAction.type}
                  onKeyCopied={handleKeyCopied}
                />
              );
            default:
              return (
                <OrderActionsModal
                  open={orderbookAction.open}
                  type={orderbookAction.type}
                  close={() => setOrderbookAction({open: false, type: null})}
                  goBack={
                    ['STATEMENT', 'HISTORY'].includes(orderbookAction.type)
                      ? () =>
                          setOrderbookAction({
                            open: true,
                            type: 'BROKER_DOWNLOAD_OPTIONS',
                          })
                      : undefined
                  }
                />
              );
          }
        })()}
      />
      <BrokerPortfolioModal
        open={isBrokerModalOpen}
        onClose={() => setIsBrokerModalOpen(false)}
      />
      {sendNotificationModal}
    </div>
  );
};

export default ViewOrders;
